import { useState } from 'react'
import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Label } from '@/components/ui/label'

/**
 * ReportAcknowledgmentModal — Professional acknowledgment before report generation.
 *
 * CONTROLLED gate (REPORTGATE-1). The parent opens it at the moment a document is
 * about to be generated, with an engagement already selected. It deliberately does
 * NOT wrap the page: wrapping meant the gate mounted before any deal was chosen, so
 * it posted an empty engagement_id, and its Cancel button dismissed the gate and
 * revealed the very page it was meant to protect.
 *
 * The parent must run the gated action ONLY from onAcknowledged. onCancel means the
 * action does not run.
 *
 * Props:
 *   open (boolean)          — whether the gate is displayed
 *   engagementId (string)   — QoE deal_id (or OTCID engagement_id) being acknowledged
 *   onAcknowledged ()       — acknowledgment recorded; the gated action may proceed
 *   onCancel ()             — user backed out; the gated action must NOT proceed
 */
export function ReportAcknowledgmentModal({
  open,
  engagementId,
  onAcknowledged,
  onCancel,
}: {
  open: boolean
  engagementId: string
  onAcknowledged: () => void
  onCancel: () => void
}) {
  const [professionalJudgmentChecked, setProfessionalJudgmentChecked] = useState(false)
  const [responsibilityChecked, setResponsibilityChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  const canContinue = professionalJudgmentChecked && responsibilityChecked && !submitting

  const resetChecks = () => {
    setProfessionalJudgmentChecked(false)
    setResponsibilityChecked(false)
    setError(null)
  }

  const handleCancel = () => {
    resetChecks()
    onCancel()
  }

  const handleContinue = async () => {
    if (!canContinue) return
    if (!engagementId) {
      setError('Select an engagement before generating a report.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.post('/v1/acknowledgments/report', {
        engagement_id: engagementId,
        acknowledgment_type: 'report_generation',
      })
      resetChecks()
      onAcknowledged()
    } catch (err: unknown) {
      // Surface the real reason — the old generic string hid 404s (deal not found)
      // and 401s (expired session) behind "please try again", which never worked.
      setError(`We could not record your acknowledgment. ${formatApiError(err)}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Analytical Tool Notice — Required Acknowledgment</CardTitle>
          <CardDescription>
            Please review before proceeding with report generation
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm space-y-3">
            <p className="text-blue-900">
              • This Quality-of-Earnings analysis is generated by Yansar's platform as an
              analytical tool for licensed professionals.
            </p>
            <p className="text-blue-900">
              • This output does not constitute accounting, legal, tax, financial, or investment advice.
            </p>
            <p className="text-blue-900">
              • You are solely responsible for reviewing, verifying, and applying professional
              judgment to all findings before use in any client engagement.
            </p>
            <p className="text-blue-900">
              • AI-generated findings have been processed through Yansar's scoring threshold registry.
              Human professional review is required before reliance.
            </p>
          </div>

          <Label className="items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 rounded border-input"
              checked={professionalJudgmentChecked}
              onChange={e => setProfessionalJudgmentChecked(e.target.checked)}
            />
            <span className="font-normal">
              I understand this is an analytical tool and will apply my professional judgment
              to all outputs before reliance.
            </span>
          </Label>

          <Label className="items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 rounded border-input"
              checked={responsibilityChecked}
              onChange={e => setResponsibilityChecked(e.target.checked)}
            />
            <span className="font-normal">
              I acknowledge my sole responsibility for reviewing and verifying the accuracy
              and completeness of all findings before client use.
            </span>
          </Label>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
        <CardFooter className="justify-end gap-3">
          <Button variant="outline" data-testid="ack-cancel" onClick={handleCancel}>
            Cancel
          </Button>
          <Button data-testid="ack-continue" disabled={!canContinue} onClick={handleContinue}>
            {submitting ? 'Saving…' : 'I Acknowledge'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
